import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCopy, FiExternalLink, FiPhone, FiShoppingBag } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { IconButton } from '../../../../components/Buttons/IconButton';
import { ModalWithTopLogo } from '../../../../components/Modals/ModalWithTopLogo';
import EventSender, {
  OfferCallUsClickedEvent,
  OfferShopOnlineClickedEvent,
} from '../../../../services/trackevent';
import * as c from '../../../../utils/constants';
import { hyphenate } from '../../../../utils/hyphenateText';
import { BlockBamOfferModal } from '../BlockBamOfferModal';
import styles from './RedeemedOfferModal.module.css';

export const RedeemedOfferModal = ({
  isOpen,
  closeModal,
  description,
  merchantLogo,
  title,
  codeDetails,
  brandColor,
  terms,
  type,
  merchantId,
  offerId,
  seasonCampaignId,
  clusterId,
  redeemedDate,
}) => {
  const { t } = useTranslation('common');
  const { firstName, lastName } = useSelector((state) => state.persist.session);
  const [bamModalVisible, setBamModalVisible] = useState(false);
  const resolveIcon = () => {
    if (type === c.OFFER_TYPES.PHONE) return FiPhone;
    if (type === c.OFFER_TYPES.ONLINE) return FiCopy;
    if (type === c.OFFER_TYPES.BAM) return FiShoppingBag;
    return FiExternalLink;
  };

  const shopOnlineAction = () => {
    EventSender.sendEvent(
      OfferShopOnlineClickedEvent(merchantId, offerId, seasonCampaignId),
    );
    navigator.clipboard.writeText(codeDetails.onlineCode).then(
      () => {
        /* clipboard successfully set */
        window.open(codeDetails.onlineUrl, '_blank');
      },
      (e) => {
        /* clipboard write failed */
        toast.error(e);
      },
    );
  };
  const callUsAction = () => {
    EventSender.sendEvent(OfferCallUsClickedEvent(merchantId, offerId));
    window.open(`tel:${codeDetails.phoneNumber}`);
  };

  const resolveAction = () => {
    if (type === c.OFFER_TYPES.PHONE) callUsAction();
    if (type === c.OFFER_TYPES.ONLINE) shopOnlineAction();
    if (type === c.OFFER_TYPES.BAM) setBamModalVisible(true);
  };

  const resolveButtonText = () => {
    if (type === c.OFFER_TYPES.PHONE)
      return t('OffersView.offerCard.callUs.modal.callUsButton', {
        phoneNumber: codeDetails.phoneNumber,
      });
    if (type === c.OFFER_TYPES.ONLINE)
      return t(
        'OffersView.offerCard.shopOnline.modal.shopOnlineWithCodeButton',
      );
    if (type === c.OFFER_TYPES.BAM)
      return t('OffersView.offerCard.shopInStore.button');
  };

  return (
    <>
      <ModalWithTopLogo
        closeModal={closeModal}
        isOpen={isOpen}
        logo={merchantLogo}
        hasCloseButton
        style={{
          overlay: { overflowY: 'auto' },
          content: { width: '428px', top: '100px' },
        }}>
        <h4 className={styles.title}>{hyphenate(title, 18)}</h4>
        <p className={styles.description}>{description}</p>
        <div className={styles.labelUsedContainer}>
          <p className={styles.labelUsed}>
            {type === c.OFFER_TYPES.BAM
              ? t('OffersView.redeemedOfferModal.labelUsedBam')
              : t('OffersView.redeemedOfferModal.labelUsedOther')}
          </p>
        </div>
        <div className={styles.greyContainer}>
          <div className={styles.infoContainer}>
            <div className={styles.infoRow}>
              <p className={styles.infoBold}>
                {t('OffersView.redeemedOfferModal.infoDate')}
              </p>
              <p className={styles.infoText}>{redeemedDate}</p>
            </div>
            <div className={styles.infoRow}>
              <p className={styles.infoBold}>
                {t('OffersView.redeemedOfferModal.infoName')}
              </p>
              <p className={styles.infoText}>{`${firstName} ${lastName}`}</p>
            </div>
            {type !== c.OFFER_TYPES.BAM && (
              <div className={styles.infoRow}>
                <p className={styles.infoBold}>
                  {t('OffersView.redeemedOfferModal.infoCode')}
                </p>
                <p className={styles.infoText}>
                  {codeDetails.onlineCode ?? codeDetails.phoneCode}
                </p>
              </div>
            )}
          </div>
          <h5 className={styles.termsTitle}>
            {t('OffersView.redeemedOfferModal.termsTitle')}
          </h5>
          <p className={styles.termsText}>{terms}</p>
          <h5 className={styles.termsTitle}>
            {t('OffersView.redeemedOfferModal.offerNotUsedTitle')}
          </h5>
          <p className={styles.termsText}>
            {t('OffersView.redeemedOfferModal.offerNotUsedText')}
          </p>
          <IconButton
            title={resolveButtonText()}
            icon={resolveIcon()}
            isFilled
            color={brandColor}
            style={{
              width: '100%',
              maxWidth: 'none',
              paddingLeft: '24px',
              paddingRight: '24px',
              marginTop: '32px',
            }}
            onClick={resolveAction}
          />
        </div>
      </ModalWithTopLogo>
      <BlockBamOfferModal
        isOpen={bamModalVisible}
        closeModal={() => setBamModalVisible(false)}
        title={t('OffersView.offerCard.shopInStore.modal.title')}
        description={t('OffersView.offerCard.shopInStore.modal.description')}
        clusterId={clusterId}
        merchantId={merchantId}
        offerId={offerId}
        seasonCampaignId={seasonCampaignId}
      />
    </>
  );
};

RedeemedOfferModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  merchantLogo: PropTypes.string.isRequired,
  brandColor: PropTypes.string,
  codeDetails: PropTypes.object,
  terms: PropTypes.string,
  type: PropTypes.string,
  clusterId: PropTypes.string,
  merchantId: PropTypes.string,
  offerId: PropTypes.string,
  seasonCampaignId: PropTypes.string,
  redeemedDate: PropTypes.string,
};
RedeemedOfferModal.defaultProps = {
  brandColor: '#595478',
};
